import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable, from } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  COMMENTS,
  FETCH_CUSTOMER,
  FETCH_PARTNER,
  GENERATE_STATEMENT_PDF,
  LEAD,
  LIST_CUSTOMERS,
  PARTNER_BILLS,
  PARTNER_DETAILS,
  PARTNER_INVOICES,
  PARTNER_JOURNAL_ENTRIES,
  PARTNER_NOTES,
  PARTNER_PAYMENTS,
  PARTNER_PURCHASE_ORDERS,
  PARTNER_QUOTES,
  PARTNER_WORK_ORDERS,
} from '../graphql/query/customer.query';
import {
  CREATE_COMMENT,
  CREATE_CONTACT,
  CREATE_CUSTOMER,
  CREATE_DOCUMENT,
  CREATE_PARTNER,
  REMOVE_CONTACT,
  REMOVE_CUSTOMER,
  REMOVE_CUSTOMER_DOCUMENT,
  REMOVE_DOCUMENT,
  REMOVE_PARTNER,
  UPDATE_CONTACT,
  UPDATE_CUSTOMER,
  UPDATE_PARTNER,
} from '../graphql/mutation/customer.mutation';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class CustomerService {
  constructor(private apollo: Apollo) {}

  fetchCustomers(search: any): Observable<any> {
    return this.apollo
      .use('project')
      .watchQuery<any>({
        query: LIST_CUSTOMERS,
        variables: {
          search,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map((result) => result.data?.customers || []));
  }

  fetchPartnerById(id: any): Observable<any> {
    return this.apollo
      .use('project')
      .watchQuery<any>({
        query: FETCH_PARTNER,
        variables: {
          id,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map((result) => result.data?.partner || []));
  }

  partnerDetails(id: any, startDate: any, endDate: any): Observable<any> {
    return this.apollo
      .use('project')
      .query<any>({
        query: PARTNER_DETAILS,
        variables: {
          id,
          startDate,
          endDate,
        },
        fetchPolicy: 'network-only',
      })
      .pipe(map((result) => result.data?.partnerDetails || []));
  }
  generatePartnerStatementPdf(
    id: any,
    startDate: any,
    endDate: any
  ): Observable<any> {
    return this.apollo
      .use('project')
      .query<any>({
        query: GENERATE_STATEMENT_PDF,
        variables: {
          id,
          startDate,
          endDate,
        },
        fetchPolicy: 'network-only',
      })
      .pipe(map((result) => result.data?.generatePartnerStatementPdf || []));
  }

  createPartner(
    createPartnerInput: any,
    createContactInputs: any,
    createAddressInputs: any,
    createRemarkInput: any,
    createDocumentInputs: any,
    employeeId: any
  ): Observable<any> {
    return this.apollo
      .use('project')
      .mutate({
        mutation: CREATE_PARTNER,
        variables: {
          createPartnerInput,
          ...(createContactInputs?.length > 0 && { createContactInputs }),
          ...(createAddressInputs?.length > 0 && { createAddressInputs }),
          ...(createDocumentInputs?.length > 0 && { createDocumentInputs }),
          ...(Object.keys(createRemarkInput).length && { createRemarkInput }),
          employeeId,
        },
      })
      .pipe(map((result: any) => result.data.createPartner || []));
  }

  updatePartner(
    updatePartnerInput: any,
    updateContactInputs: any,
    updateAddressInputs: any,
    updateRemarkInput: any,
    employeeId: any,
    updateDocumentInputs: any
  ): Observable<any> {
    return this.apollo
      .use('project')
      .mutate({
        mutation: UPDATE_PARTNER,
        variables: {
          updatePartnerInput,
          ...(updateContactInputs?.length > 0 && { updateContactInputs }),
          ...(updateAddressInputs?.length > 0 && { updateAddressInputs }),
          employeeId,
          ...(Object.keys(updateRemarkInput).length && { updateRemarkInput }),
          // ...(Object.keys(updateDocumentInput).length && { updateDocumentInput })
          ...(updateDocumentInputs?.length > 0 && { updateDocumentInputs }),
        },
      })
      .pipe(map((result: any) => result.data.updatePartner || []));
  }

  removePartner(id: any): Observable<any> {
    return this.apollo
      .use('project')
      .mutate({
        mutation: REMOVE_PARTNER,
        variables: {
          id,
        },
        fetchPolicy: 'network-only',
      })
      .pipe(map((result: any) => result.data.removePartner || []));
  }

  removeDocument(id: any): Observable<any> {
    return this.apollo
      .use('project')
      .mutate({
        mutation: REMOVE_DOCUMENT,
        variables: {
          id,
        },
        fetchPolicy: 'network-only',
      })
      .pipe(map((result: any) => result.data.removeDocument || []));
  }

  createDocument(createDocumentInput: any): Observable<any> {
    return this.apollo
      .use('project')
      .mutate({
        mutation: CREATE_DOCUMENT,
        variables: {
          createDocumentInput,
        },
        fetchPolicy: 'network-only',
      })
      .pipe(map((result: any) => result.data.createDocument || []));
  }

  createComment(createCommentInput: any): Observable<any> {
    return this.apollo
      .use('accounts')
      .mutate({
        mutation: CREATE_COMMENT,
        variables: {
          createCommentInput,
        },
        fetchPolicy: 'network-only',
      })
      .pipe(map((result: any) => result.data.createComment || []));
  }

  createContact(createContactInput: any): Observable<any> {
    return this.apollo
      .use('project')
      .mutate({
        mutation: CREATE_CONTACT,
        variables: {
          createContactInput,
        },
        fetchPolicy: 'network-only',
      })
      .pipe(map((result: any) => result.data.createContact || []));
  }

  updateContact(updateContactInput: any): Observable<any> {
    return this.apollo
      .use('project')
      .mutate({
        mutation: UPDATE_CONTACT,
        variables: {
          updateContactInput,
        },
        fetchPolicy: 'network-only',
      })
      .pipe(map((result: any) => result.data.updateContact || []));
  }

  removeContact(id: any): Observable<any> {
    return this.apollo
      .use('project')
      .mutate({
        mutation: REMOVE_CONTACT,
        variables: {
          id,
        },
        fetchPolicy: 'network-only',
      })
      .pipe(map((result: any) => result.data.removeContact || []));
  }

  comments(conditions: any): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: COMMENTS,
        variables: {
          conditions,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map((result) => result.data?.comments || []));
  }

  bulkCreateCustomer(file: File): Observable<any> {
    const token = localStorage.getItem('AUTH_TOKEN');
    const formData = new FormData();
    formData.append(
      'operations',
      JSON.stringify({
        query: `
          mutation bulkCreatePartners($file: Upload!) {
            bulkCreatePartners(file: $file)
          }
          `,
        variables: { file: null },
      })
    );

    formData.append('map', JSON.stringify({ '0': ['variables.file'] }));
    formData.append('0', file);

    return from(
      fetch(environment.url.projectModuleUrl, {
        method: 'POST',
        body: formData,
        headers: {
          authorization: token ? `Bearer ${token}` : '',
          'x-apollo-operation-name': 'uploadFile',
        },
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        })
        .then((result) => result.data?.bulkCreatePartners || result)
    );
  }

  findOnePartnerForBills(id: any): Observable<any> {
    return this.apollo
      .use('project')
      .watchQuery<any>({
        query: PARTNER_BILLS,
        variables: {
          id,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((result) => result.data?.findOnePartnerForBills || [])
      );
  }
  findOnePartnerForInvoices(id: any): Observable<any> {
    return this.apollo
      .use('project')
      .query<any>({
        query: PARTNER_INVOICES,
        variables: {
          id,
        },
        fetchPolicy: 'no-cache',
      })
      .pipe(map((result) => result?.data?.findOnePartnerForInvoices));
  }
  findOnePartnerForJournalEntries(id: any): Observable<any> {
    return this.apollo
      .use('project')
      .watchQuery<any>({
        query: PARTNER_JOURNAL_ENTRIES,
        variables: {
          id,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((result) => result.data?.findOnePartnerForJournalEntries || [])
      );
  }
  findOnePartnerForPurchaseOrders(id: any): Observable<any> {
    return this.apollo
      .use('project')
      .watchQuery<any>({
        query: PARTNER_PURCHASE_ORDERS,
        variables: {
          id,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((result) => result.data?.findOnePartnerForPurchaseOrders || [])
      );
  }
  findOnePartnerForNotes(id: any): Observable<any> {
    return this.apollo
      .use('project')
      .watchQuery<any>({
        query: PARTNER_NOTES,
        variables: {
          id,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((result) => result.data?.findOnePartnerForNotes || [])
      );
  }
  findOnePartnerForQuotes(id: any): Observable<any> {
    return this.apollo
      .use('project')
      .watchQuery<any>({
        query: PARTNER_QUOTES,
        variables: {
          id,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((result) => result.data?.findOnePartnerForQuotes || [])
      );
  }
  findOnePartnerForWorkorders(id: any): Observable<any> {
    return this.apollo
      .use('project')
      .watchQuery<any>({
        query: PARTNER_WORK_ORDERS,
        variables: {
          id,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((result) => result.data?.findOnePartnerForWorkorders || [])
      );
  }
  findOnePartnerForPayments(id: any): Observable<any> {
    return this.apollo
      .use('project')
      .watchQuery<any>({
        query: PARTNER_PAYMENTS,
        variables: {
          id,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((result) => result.data?.findOnePartnerForPayments || [])
      );
  }

  fetchCustomerById(id: any): Observable<any> {
    return this.apollo
      .use('project')
      .watchQuery<any>({
        query: FETCH_CUSTOMER,
        variables: {
          id,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map((result) => result.data?.customer || []));
  }

  createCustomer(createCustomerInput: any, customerDocs: any): Observable<any> {
    return this.apollo
      .use('project')
      .mutate({
        mutation: CREATE_CUSTOMER,
        variables: {
          createCustomerInput,
          customerDocs,
        },
      })
      .pipe(map((result: any) => result.data.createCustomer || []));
  }

  updateCustomer(
    updateCustomerInput: any,
    customerDocs: any,
    id: Number
  ): Observable<any> {
    return this.apollo
      .use('project')
      .mutate({
        mutation: UPDATE_CUSTOMER,
        variables: {
          updateCustomerInput,
          customerDocs,
          id,
        },
      })
      .pipe(map((result: any) => result.data.updateCustomer || []));
  }

  removeCustomer(id: any): Observable<any> {
    return this.apollo
      .use('project')
      .mutate({
        mutation: REMOVE_CUSTOMER,
        variables: {
          id,
        },
        fetchPolicy: 'network-only',
      })
      .pipe(map((result: any) => result.data.removeCustomer || []));
  }

  removeCustomerDocument(id: any): Observable<any> {
    return this.apollo
      .use('project')
      .mutate({
        mutation: REMOVE_CUSTOMER_DOCUMENT,
        variables: {
          id,
        },
        fetchPolicy: 'network-only',
      })
      .pipe(map((result: any) => result.data.removeCustomerDoc || []));
  }

    fetchLeadById(id: any): Observable<any> {
      return this.apollo
        .use('project')
        .watchQuery<any>({
          query: LEAD,
          variables: {
            id,
          },
          fetchPolicy: 'network-only',
        })
        .valueChanges.pipe(map((result) => result.data?.lead || []));
    }
  
}
